import { useCallback, useMemo, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Trans, useLingui } from '@lingui/react/macro'
import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'

import {
  Delete02Icon,
  PencilEdit01Icon,
  StackStarIcon
} from '@hugeicons/core-solid-rounded'

import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Suspense } from '@/components/ui/Suspense'
import UpDownButtons from './UpDownButtons'
import useCategory from '../hooks/useCategory'
import GoodieRow from './GoodieRow'
import { IconButton } from '@/components/ui/IconButton'
import { EmptyView } from '@/components/ui/EmptyView'

interface Props {
  layoutId: number
  slug: string
  readOnly: boolean
}

type HeaderItem = {
  type: 'header'
  value: string
}

function isHeaderItem(
  item: HeaderItem | GraphQL.CategoryGoodie
): item is HeaderItem {
  return 'type' in item && item.type === 'header'
}

export function CategoryDetail({ layoutId, slug, readOnly }: Props) {
  const { t } = useLingui()

  const { category, addGoodies, moveGoodies, removeGoodies } = useCategory(
    layoutId,
    slug,
    true
  )

  const [selectedGoodies, setSelectedGoodies] = useState<Set<number>>(new Set())

  const isAlphabeticallySorted =
    category.sortBy === GraphQL.CategorySortTypes.Alphabetically

  const items: (HeaderItem | GraphQL.CategoryGoodie)[] = useMemo(() => {
    if (!category) return []

    const categoryGoodies = category.categoryGoodies
      .map(({ goodie, ...rest }) => {
        return {
          ...rest,
          goodie: getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, goodie)
        }
      })
      .filter(({ goodie }) => goodie != null) as GraphQL.CategoryGoodie[]

    if (isAlphabeticallySorted) {
      return categoryGoodies
        .toSorted(({ goodie: gA }, { goodie: gB }) => {
          if (!gA) return -1
          if (!gB) return 1
          return gA.title.localeCompare(gB.title)
        })
        .flatMap(function (categoryGoodie, index, categoryGoodies) {
          const lastGoodie = categoryGoodies[index - 1]?.goodie
          const startLetter = categoryGoodie.goodie?.title[0].toUpperCase()
          const lastLetter = lastGoodie?.title[0].toUpperCase()

          if (startLetter !== lastLetter) {
            return [{ type: 'header', value: startLetter }, categoryGoodie]
          }

          return [categoryGoodie]
        })
    }

    return categoryGoodies.toSorted(
      ({ position: posA }, { position: posB }) => posA - posB
    )
  }, [category, isAlphabeticallySorted])

  const onToggleGoodie = useCallback(
    (id: number) => {
      setSelectedGoodies(goodies => {
        const newGoodies = new Set(goodies)

        if (!newGoodies.delete(id)) newGoodies.add(id)
        return newGoodies
      })
    },
    [setSelectedGoodies]
  )

  const onRemove = useCallback(() => {
    if (selectedGoodies.size === 0) return

    removeGoodies(selectedGoodies)
  }, [selectedGoodies, removeGoodies])

  const onChangePosition = useCallback(
    (position: number) => {
      moveGoodies(selectedGoodies, position)
    },
    [selectedGoodies, moveGoodies]
  )

  const onUp = useCallback(() => {
    if (!category) return

    const position = category.categoryGoodies.findIndex(({ goodieId }) =>
      selectedGoodies.has(goodieId)
    )

    onChangePosition(position - 1)
  }, [category, selectedGoodies, onChangePosition])

  const onDown = useCallback(() => {
    if (!category) return

    const position = category.categoryGoodies.findLastIndex(({ goodieId }) =>
      selectedGoodies.has(goodieId)
    )

    onChangePosition(position + 2 - selectedGoodies.size)
  }, [category, selectedGoodies, onChangePosition])

  if (!category) return null

  const categoryName = category.name
  const selectedSize = selectedGoodies.size

  return (
    <>
      <header className="flex flex-row items-center gap-4">
        {!readOnly && (
          <IconButton
            icon={PencilEdit01Icon}
            aria-label={t`Add/Remove Category Goodies`}
            onClick={addGoodies}
          />
        )}
        <h2 className="flex-1 text-heading2 text-text">{categoryName}</h2>
        {selectedGoodies.size > 0 && (
          <div className="flex flex-row items-center gap-2">
            <UpDownButtons onUp={onUp} onDown={onDown} />
            <ConfirmDialog
              title={t`Remove Goodies`}
              message={t`Are you sure that you want to remove ${selectedSize} goodies from the category ${categoryName}?`}
              onConfirm={onRemove}
            >
              <div className="size-5 text-text" aria-label={t`Remove Goodies`}>
                <HugeiconsIcon icon={Delete02Icon} className="size-full" />
              </div>
            </ConfirmDialog>
          </div>
        )}
      </header>
      <div className="flex flex-1 flex-col">
        {items.length === 0 && (
          <EmptyView Icon={StackStarIcon} text={t`No Goodies yet`}>
            <button
              tabIndex={0}
              onClick={addGoodies}
              className="text-info-500 pointer-events-auto"
            >
              <Trans>Add/Remove Category Goodies</Trans>
            </button>
          </EmptyView>
        )}
        {items.map(function (item, index) {
          if (isHeaderItem(item)) {
            return (
              <h4
                className="text-heading2 text-text-light"
                key={`${item.type}_${item.value}`}
              >
                {item.value}
              </h4>
            )
          }

          const { goodieId } = item

          return (
            <GoodieRow
              key={goodieId}
              id={goodieId}
              position={index}
              selected={selectedGoodies.has(goodieId)}
              onSelected={() => onToggleGoodie(goodieId)}
              onChangePosition={pos => moveGoodies(new Set([goodieId]), pos)}
              readOnly={readOnly || isAlphabeticallySorted}
            />
          )
        })}
      </div>
    </>
  )
}

export default function CategoryDetailSuspense(props: Props) {
  return (
    <section className="flex flex-1 flex-col gap-2.5 px-2.5">
      <Suspense>
        <CategoryDetail {...props} />
      </Suspense>
    </section>
  )
}
